import { useMemo, useState } from 'react';
import { useListStepTypes } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import {
  DatabaseIcon,
  ArrowsLeftRightIcon,
  PlugIcon,
  GitBranchIcon,
  WrenchIcon,
  LightningIcon,
  CircleNotchIcon,
  DotsSixVerticalIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import type { ListStepTypesResponse } from '@erp/rpc';

type StepType = ListStepTypesResponse['stepTypes'][0];

const categoryIcons: Record<string, React.ReactNode> = {
  'Data Access': <DatabaseIcon className="w-4 h-4" />,
  Transformation: <ArrowsLeftRightIcon className="w-4 h-4" />,
  Integration: <PlugIcon className="w-4 h-4" />,
  'Control Flow': <GitBranchIcon className="w-4 h-4" />,
  Utility: <WrenchIcon className="w-4 h-4" />,
  Trigger: <LightningIcon className="w-4 h-4" />,
};

const categoryColors: Record<string, string> = {
  'Data Access':
    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  Transformation:
    'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  Integration:
    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  'Control Flow':
    'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  Utility:
    'bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800',
  Trigger:
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
};

interface DraggableStepCardProps {
  stepType: StepType;
}

function DraggableStepCard({ stepType }: DraggableStepCardProps) {
  const handleDragStart = (event: React.DragEvent) => {
    event.dataTransfer.setData(
      'application/workflow-step-type',
      JSON.stringify(stepType),
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  const colorClass =
    categoryColors[stepType.category] || categoryColors['Utility'];

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className={cn(
        'group flex items-center gap-2 p-2 rounded-md border cursor-grab active:cursor-grabbing',
        'bg-white dark:bg-slate-800 hover:shadow-md transition-all duration-150',
        'hover:scale-[1.02] hover:border-primary/50',
      )}
    >
      <DotsSixVerticalIcon className="w-4 h-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('p-1 rounded flex-shrink-0', colorClass)}>
            {categoryIcons[stepType.category] || categoryIcons['Utility']}
          </span>
          <span className="text-sm font-medium truncate">{stepType.name}</span>
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {stepType.description}
        </p>
      </div>
    </div>
  );
}

interface NodeLibraryContentProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export function NodeLibraryContent({
  searchQuery,
  onSearchChange,
}: NodeLibraryContentProps) {
  const { data, isLoading, error } = useListStepTypes();
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const { stepsByCategory, categories } = useMemo(() => {
    if (!data?.stepTypes)
      return {
        stepsByCategory: new Map<string, StepType[]>(),
        categories: [],
      };

    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? data.stepTypes.filter(
          (st) =>
            st.name.toLowerCase().includes(query) ||
            st.description.toLowerCase().includes(query) ||
            st.type.toLowerCase().includes(query),
        )
      : data.stepTypes;

    const grouped = new Map<string, StepType[]>();
    filtered.forEach((stepType) => {
      const category = stepType.category;
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(stepType);
    });

    return {
      stepsByCategory: grouped,
      categories: Array.from(grouped.keys()).sort(),
    };
  }, [data?.stepTypes, searchQuery]);

  const effectiveOpenCategories = searchQuery.trim()
    ? categories
    : openCategories;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <CircleNotchIcon className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-sm text-destructive">
        Failed to load step types
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {categories.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {searchQuery
              ? 'No nodes match your search'
              : 'No step types available'}
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={effectiveOpenCategories}
            onValueChange={setOpenCategories}
            className="px-2"
          >
            {categories.map((category) => {
              const steps = stepsByCategory.get(category) || [];
              const colorClass =
                categoryColors[category] || categoryColors['Utility'];

              return (
                <AccordionItem
                  key={category}
                  value={category}
                  className="border-b-0"
                >
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className={cn('p-1 rounded', colorClass)}>
                        {categoryIcons[category] || categoryIcons['Utility']}
                      </span>
                      <span className="text-sm font-medium">{category}</span>
                      <Badge variant="secondary" className="text-xs ml-1">
                        {steps.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-2">
                    <div className="space-y-2">
                      {steps.map((stepType) => (
                        <DraggableStepCard
                          key={stepType.type}
                          stepType={stepType}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      <div className="px-3 py-2 border-t flex-shrink-0">
        <p className="text-[10px] text-muted-foreground text-center">
          Drag and drop nodes onto the canvas
        </p>
      </div>
    </div>
  );
}
